import React, { useContext, useState } from "react";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ProfileCard from "./ProfileCard";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ImsLogo } from "../../assets";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  BusinessType,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  usePlatformConfig,
  PAGE_PORTAL_COMPANY,
  PAGE_PORTAL_DASHBOARD,
  PORTAL_PREFIX
} from "@kivunova/kivufrontendcommon";
import { getSidebarItems } from "../../navigation/sidebarByBusinessType";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { UserRole } from "../Profile/enums/profileEnums";
import { layout } from "../../theme/theme";
import {
  sidebarBrandLogoSx,
  sidebarBrandSx,
  sidebarBrandTitleSx,
  sidebarDividerSx,
  sidebarDrawerPaperSx,
  sidebarListSx,
  sidebarNavIconWrapSx,
  sidebarNavItemSx,
  sidebarSubNavItemSx
} from "./sidebarStyles";

const drawerWidth = layout.sidebarWidth;
const listItemVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.04,
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};
const collapseVariants = {
  closed: { opacity: 0, height: 0 },
  open: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

interface SideBarProps {
  setFlyOver?: (open: boolean) => void;
  /** Mobile: use temporary Drawer (slides in, overlays content, portals to body) */
  mobile?: boolean;
  open?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SideBarProps> = ({
  setFlyOver,
  mobile = false,
  open = true,
  onClose
}) => {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const profileData = useSelector(
    (state: RootState) => state.profile.profileData
  );
  const userDetails = useSelector(
    (state: RootState) => state.user?.userDetails
  );
  const { t } = useKivunovaTranslation();
  const { config, featureFlags } = usePlatformConfig();
  /** Match PortalHomePage / profile hooks — avoid menu built from stale or half-hydrated profile only. */
  const sidebarRole =
    profileData?.role ?? userDetails?.role ?? UserRole.ANALYST;
  const businessType = config?.businessType ?? BusinessType.RETAIL;
  const SideBarMenuItems = getSidebarItems(
    t,
    sidebarRole,
    businessType,
    featureFlags
  );

  const handleToggle = (menuKey: string) => {
    setOpenMenus((prev) => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };
  const { language, defaultLang } = useContext(KivuI18nContext);

  const isSelected = (path: string): boolean => {
    // Normalize paths for comparison
    const normalizedPath = localizedPath(path, language, defaultLang);
    const currentPath = location.pathname;

    // Build the dashboard path for comparison
    const dashboardPath = `${PORTAL_PREFIX}/${PAGE_PORTAL_DASHBOARD}`;
    const normalizedDashboardPath = localizedPath(
      dashboardPath,
      language,
      defaultLang
    );

    // For dashboard, use exact match only
    const isDashboard = path === dashboardPath;
    if (isDashboard) {
      return currentPath === normalizedDashboardPath;
    }

    // Company page only — not company/stores (separate nav item)
    const companyPath = `${PORTAL_PREFIX}/${PAGE_PORTAL_COMPANY}`;
    if (path === companyPath) {
      return currentPath === localizedPath(companyPath, language, defaultLang);
    }

    // For other routes, check if current path starts with the normalized path
    // (e.g. product list path matches when viewing a product under the same segment)
    // Use path boundary check to avoid false positives (e.g., "portal" shouldn't match "portals")
    return (
      currentPath === normalizedPath ||
      currentPath.startsWith(`${normalizedPath}/`)
    );
  };

  const handleClickItem = (path?: string) => {
    if (path) {
      navigate(localizedPath(path, language, defaultLang));
      setFlyOver?.(false);
    }
  };

  return (
    <Drawer
      variant={mobile ? "temporary" : "permanent"}
      open={mobile ? open : true}
      onClose={mobile ? onClose : undefined}
      data-testid="sidebar-drawer"
      ModalProps={
        mobile
          ? {
              keepMounted: true,
              slotProps: {
                root: { sx: { zIndex: 2147483647 } }
              }
            }
          : undefined
      }
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        ...(mobile && {
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            border: "none",
            boxShadow: "4px 0 24px rgba(0,0,0,0.15)"
          }
        }),
        ...(!mobile && {
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "none",
            border: "none",
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0
          }
        }),
        "& .MuiDrawer-paper": sidebarDrawerPaperSx
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Link
          to={localizedPath("/", language, defaultLang)}
          aria-label={t("sidebar-brand-home", "Kivunova FMS home")}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Box sx={sidebarBrandSx}>
            <Avatar src={ImsLogo} sx={sidebarBrandLogoSx} />
            <Typography component="span" sx={sidebarBrandTitleSx}>
              {t("sidebar-brand-title", "Kivunova FMS")}
            </Typography>
          </Box>
        </Link>

        <Divider sx={sidebarDividerSx} />

        {/* Main Menu - scrollable on mobile */}
        <List sx={sidebarListSx}>
          {SideBarMenuItems.map((item, index) => {
            const hasChildren =
              Array.isArray(item.items) && item.items.length > 0;
            const open = openMenus[item.testId] || false;
            const itemSelected = isSelected(item.path);

            return (
              <motion.div
                key={item.testId}
                custom={index}
                variants={listItemVariants}
                initial="hidden"
                animate="visible"
              >
                <ListItemButton
                  data-testid={item.testId}
                  onClick={() =>
                    hasChildren
                      ? handleToggle(item.testId)
                      : handleClickItem(item.path)
                  }
                  component={!hasChildren ? Link : "button"}
                  to={
                    !hasChildren
                      ? localizedPath(item.path, language, defaultLang)
                      : undefined
                  }
                  selected={itemSelected}
                  sx={{
                    ...sidebarNavItemSx(itemSelected),
                    width: "100%",
                    "&.Mui-selected": sidebarNavItemSx(true),
                    "&.Mui-selected:hover": sidebarNavItemSx(true)
                  }}
                >
                  <ListItemIcon sx={{ color: "#fff", minWidth: 44 }}>
                    <Box
                      className="sidebar-nav-icon-wrap"
                      sx={sidebarNavIconWrapSx(itemSelected)}
                    >
                      {item.icon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: itemSelected ? 700 : 500,
                      letterSpacing: "-0.01em"
                    }}
                  />
                  {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>

                <AnimatePresence initial={false}>
                  {hasChildren && open && (
                    <motion.div
                      key={`collapse-${item.testId}`}
                      variants={collapseVariants}
                      initial="closed"
                      animate="open"
                      exit="closed"
                    >
                      <List
                        component="div"
                        disablePadding
                        sx={{ pl: 2, pr: 1 }}
                      >
                        {item?.items?.map((subItem) => {
                          const subSelected = isSelected(subItem.path);

                          return (
                            <ListItemButton
                              key={subItem.testId}
                              data-testid={subItem.testId}
                              component={Link}
                              to={localizedPath(
                                subItem.path,
                                language,
                                defaultLang
                              )}
                              onClick={() => setFlyOver?.(false)}
                              selected={subSelected}
                              sx={{
                                ...sidebarSubNavItemSx(subSelected),
                                "&.Mui-selected": sidebarSubNavItemSx(true),
                                "&.Mui-selected:hover":
                                  sidebarSubNavItemSx(true)
                              }}
                            >
                              <ListItemIcon
                                sx={{ color: "#fff", minWidth: 36 }}
                              >
                                <Box sx={sidebarNavIconWrapSx(subSelected)}>
                                  {subItem.icon}
                                </Box>
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.label}
                                primaryTypographyProps={{
                                  fontSize: "0.8125rem",
                                  fontWeight: subSelected ? 600 : 500
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </List>

        <Divider sx={{ ...sidebarDividerSx, mb: 0.5 }} />
        <ProfileCard />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
